import { useEffect, useMemo, useState } from "react";
import { auditHistoricalYieldEvidence } from "../domain/historicalYieldArithmetic";
import { supabase } from "../lib/supabase";
import type { AppProfile } from "../types";

type YieldRow = {
  id: string;
  quantity: number;
  quantity_unit: string;
  review_status: "pending" | "mapped" | "rejected";
  review_note: string | null;
  mapped_composition_id: string | null;
  suggested_work_item_id: string | null;
  suggested_work_name: string | null;
  mapped_work_name: string | null;
  composition_version: number | null;
  context_note: string;
  source_file: string;
  source_year: number | null;
  sheet_name: string | null;
  cell_ref: string | null;
  article_code: string | null;
  original_description: string | null;
  evidence: string | null;
  raw_comment: string | null;
};
type WorkItem = { id: string; name: string; code: string };
type Composition = { id: string; work_item_id: string; version: number; status: string };

export default function HistoricalYieldReview({ profile }: { profile: AppProfile }) {
  const canReview = profile.role === "admin" || profile.role === "estimator";
  const [rows, setRows] = useState<YieldRow[]>([]);
  const [works, setWorks] = useState<WorkItem[]>([]);
  const [compositions, setCompositions] = useState<Composition[]>([]);
  const [selection, setSelection] = useState<Record<string, string>>({});
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [filter, setFilter] = useState("all");
  const [busy, setBusy] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  async function load() {
    setBusy(true);
    const results = await Promise.all([
      supabase.from("historical_yield_review").select("*").order("source_year", { ascending: false }).order("id"),
      supabase.from("work_items").select("id,name,code").eq("is_active", true).order("code"),
      supabase.from("work_item_compositions").select("id,work_item_id,version,status").in("status", ["draft", "approved"]).order("version", { ascending: false }),
    ]);
    const problem = results.find((result) => result.error)?.error;
    if (problem) { setError(problem.message); setRows([]); }
    else {
      setRows((results[0].data ?? []) as YieldRow[]);
      setWorks((results[1].data ?? []) as WorkItem[]);
      setCompositions((results[2].data ?? []) as Composition[]);
    }
    setBusy(false);
  }
  useEffect(() => { void load(); }, []);

  const options = useMemo(() => compositions.flatMap((composition) => {
    const work = works.find((item) => item.id === composition.work_item_id);
    return work ? [{ ...composition, work }] : [];
  }), [works, compositions]);

  async function review(row: YieldRow, status: YieldRow["review_status"]) {
    if (!canReview || saving) return;
    const compositionId = selection[row.id] ?? row.mapped_composition_id ?? "";
    if (status === "mapped" && !compositionId) return;
    setSaving(row.id); setError(""); setNotice("");
    try {
      const { error: rpcError } = await supabase.rpc("review_historical_yield", {
        observation_id: row.id,
        next_status: status,
        target_composition_id: status === "mapped" ? compositionId : null,
        decision_note: notes[row.id] ?? row.review_note ?? null,
      });
      if (rpcError) throw rpcError;
      setNotice(status === "mapped"
        ? "Ligação confirmada. O rendimento continua histórico; a composição e os preços não foram alterados."
        : "Revisão registada, com histórico da decisão preservado.");
      await load();
    } catch (problem) { setError(problem instanceof Error ? problem.message : String(problem)); }
    finally { setSaving(null); }
  }

  return <section className="stack-lg">
    <header className="page-header"><div>
      <p className="eyebrow">Histórico técnico L37</p>
      <h2>Rendimentos históricos</h2>
      <p className="muted">Consumos de aço em kg/m³, separados por elemento e obra. A ligação a uma composição exige revisão e não altera o seu rendimento aprovado.</p>
    </div></header>
    <div className="historical-stats">
      <article className="card"><span>Observações</span><strong>{rows.length}</strong></article>
      <article className="card"><span>Por rever</span><strong>{rows.filter((row) => row.review_status === "pending").length}</strong></article>
      <article className="card"><span>Ligadas</span><strong>{rows.filter((row) => row.review_status === "mapped").length}</strong></article>
    </div>
    {error ? <div className="alert alert-error">{error}</div> : null}
    {notice ? <div className="alert alert-success">{notice}</div> : null}
    <label><span>Estado</span><select value={filter} onChange={(event) => setFilter(event.target.value)}>
      <option value="all">Todos</option><option value="pending">Por rever</option><option value="mapped">Ligados</option><option value="rejected">Rejeitados</option>
    </select></label>
    {busy ? <div className="empty card">A carregar rendimentos…</div> : null}
    <div className="historical-list">{!busy && rows.filter((row) => filter === "all" || row.review_status === filter).map((row) => {
      const arithmetic = auditHistoricalYieldEvidence(row.evidence, Number(row.quantity));
      const selected = selection[row.id] ?? row.mapped_composition_id ?? "";
      return <article className="historical-candidate-card card" key={row.id}>
        <div className="historical-candidate-main">
          <div className="result-meta"><span>{row.source_year}</span><span>{row.review_status === "mapped" ? "Ligado" : row.review_status === "rejected" ? "Rejeitado" : "Por rever"}</span></div>
          <h4>{row.original_description || "Elemento por identificar"}</h4>
          <strong>{Number(row.quantity).toLocaleString("pt-PT")} kg/m³</strong>
          <p className="muted">{row.context_note}</p>
          {row.suggested_work_name ? <p>Sugestão: {row.suggested_work_name}</p> : null}
          {row.mapped_work_name ? <p>Ligação: {row.mapped_work_name} · versão {row.composition_version}</p> : null}
          {arithmetic.status === "inconsistent" ? <div className="alert alert-error" role="status">
            <strong>A nota original contém contas que precisam de confirmação.</strong>
            <p>O rendimento e a nota foram preservados. Confirme a origem antes de ligar este rendimento a uma composição.</p>
            <details><summary>Ver diferenças aritméticas ({arithmetic.issues.length})</summary>
              {arithmetic.issues.map(issue => <p key={issue.step}>{issue.label}: cálculo {issue.expected.toLocaleString("pt-PT", { maximumFractionDigits: 4 })} {issue.unit}; nota {issue.reported.toLocaleString("pt-PT", { maximumFractionDigits: 4 })} {issue.unit}.</p>)}
            </details>
          </div> : null}
          <p className="historical-evidence">{row.evidence}</p>
          <small className="muted">{row.source_file.split("/").pop()} · {row.sheet_name} · {row.cell_ref} · artigo {row.article_code}</small>
          <details><summary>Nota original completa</summary><p style={{ whiteSpace: "pre-wrap" }}>{row.raw_comment || "Nota completa indisponível; consultar o ficheiro original."}</p></details>
        </div>
        {canReview ? <div className="historical-candidate-actions">
          <label><span>Composição de destino</span><select value={selected} disabled={saving !== null} onChange={(event) => setSelection((current) => ({ ...current, [row.id]: event.target.value }))}>
            <option value="">Selecionar após revisão…</option>
            {options.map((option) => <option value={option.id} key={option.id}>{option.work.name} · v{option.version} · {option.status === "draft" ? "rascunho" : "aprovada"}</option>)}
          </select></label>
          <label><span>Nota da decisão</span><textarea value={notes[row.id] ?? row.review_note ?? ""} disabled={saving !== null} onChange={(event) => setNotes((current) => ({ ...current, [row.id]: event.target.value }))} /></label>
          <button className="button button-primary" type="button" disabled={!selected || saving !== null} onClick={() => void review(row, "mapped")}>Confirmar ligação</button>
          <button className="button button-secondary" type="button" disabled={saving !== null} onClick={() => void review(row, "pending")}>Voltar a rever</button>
          <button className="text-button" type="button" disabled={saving !== null} onClick={() => void review(row, "rejected")}>Rejeitar</button>
        </div> : null}
      </article>;
    })}</div>
    {!busy && rows.filter((row) => filter === "all" || row.review_status === filter).length === 0 && !error ? <div className="empty card">Sem rendimentos neste estado.</div> : null}
  </section>;
}
